import { CandidatLink } from '../../components/candidat/CandidatLink';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ArrowLeft, Save, Plus, Trash2, Check, CreditCard, FileText, Loader2, Landmark, Building2, Download, FileCheck, Users, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';
import { TYPES_FINANCEMENT, MODALITES_FACTURATION, TYPES_STAGIAIRE, TYPES_ENTREPRISE } from './constants';
import { getAuthToken } from '../../lib/authToken';
import { useAuth } from '../../contexts/AuthContext';
import { peutVoirCouts } from '../../lib/roles';

const TYPES_FINANCEUR = [
  { value: 'opco', label: 'OPCO' },
  { value: 'cpf', label: 'CPF' },
  { value: 'pole_emploi', label: 'France Travail' },
  { value: 'region', label: 'Région' },
  { value: 'entreprise', label: 'Entreprise' },
  { value: 'autre', label: 'Autre' },
];

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== DOSSIERS FINANCEURS TAB ==============
// Constants imported from ./dossier/constants.js

const FinancementTab = ({ dossierId, formData, financeurs, formations, modules, clients, onFinanceursUpdated, onDfsChanged }) => {
  const { user } = useAuth();
  const voirCouts = peutVoirCouts(user);
  const lectureSeule = !voirCouts; // responsable pedagogique : consultation uniquement (modification refusee par le serveur)
  const [dossiersFinanceurs, setDossiersFinanceurs] = useState([]);
  const [naturesFinancement, setNaturesFinancement] = useState([]);
  const [entreprisesList, setEntreprisesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDf, setEditingDf] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const token = getAuthToken();
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Inline creation: Financeur
  const [financeurDialogOpen, setFinanceurDialogOpen] = useState(false);
  const [newFinanceur, setNewFinanceur] = useState({ nom: '', type_financeur: 'opco', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '' });
  const [creatingFinanceur, setCreatingFinanceur] = useState(false);

  // Inline creation: Entreprise (Client)
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({ nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  // Documents du DF
  const [dfDocuments, setDfDocuments] = useState([]);
  const [generatingDoc, setGeneratingDoc] = useState(null);
  const [deleteDocConfirm, setDeleteDocConfirm] = useState(null);
  
  // Convocation dialog
  const [convocationDialog, setConvocationDialog] = useState(false);
  const [selectedCandidatIds, setSelectedCandidatIds] = useState([]);
  const [convocationMode, setConvocationMode] = useState('groupe'); // 'groupe' or 'individuel'

  const candidatsAction = formData.candidats || [];
  const allCandidats = React.useRef([]);

  const fetchData = React.useCallback(async () => {
    try {
      const [dfRes, natRes, candRes, entRes] = await Promise.all([
        axios.get(`${API_URL}/dossiers-financeurs?action_id=${dossierId}`, { headers }),
        axios.get(`${API_URL}/natures-financement`, { headers }),
        axios.get(`${API_URL}/candidats`, { headers }),
        axios.get(`${API_URL}/entreprises`, { headers }),
      ]);
      setDossiersFinanceurs(dfRes.data);
      setNaturesFinancement(natRes.data);
      allCandidats.current = candRes.data;
      setEntreprisesList(entRes.data || []);
    } catch { toast.error('Erreur chargement dossiers financeurs'); }
    setLoading(false);
  }, [dossierId, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch documents for the currently editing DF
  const fetchDfDocuments = React.useCallback(async (dfId) => {
    if (!dfId) return;
    try {
      const res = await axios.get(`${API_URL}/factures/dossier/${dossierId}`, { headers });
      const filtered = (res.data || []).filter(d => d.dossier_financeur_id === dfId);
      setDfDocuments(filtered);
    } catch (err) { setDfDocuments([]); }
  }, [dossierId, headers]);

  useEffect(() => {
    if (editingDf?.id) fetchDfDocuments(editingDf.id);
    else setDfDocuments([]);
  }, [editingDf?.id, fetchDfDocuments]);

  // Generer un document (devis, convention)
  const handleGenerateDocument = async (type) => {
    if (!editingDf?.id) return;
    setGeneratingDoc(type);
    try {
      const df = editingDf;
      const nbCandidats = (df.candidats || []).length || df.nb_candidats_prevus || 1;
      const prixUnitaire = df.tarif_unitaire || 0;
      const totalHT = prixUnitaire * nbCandidats;
      const tauxTVA = 20;
      const montantTVA = Math.round(totalHT * tauxTVA / 100 * 100) / 100;
      const totalTTC = Math.round((totalHT + montantTVA) * 100) / 100;

      // Destinataire: subrogation → entreprise finale, sous-traitance → financeur
      const isSubrogation = (df.type_financement || '').includes('subrogation');
      let destNom = '';
      let destType = '';
      let destId = '';
      let destAdresse = '';
      let destContact = '';

      if (isSubrogation) {
        // Subrogation: devis au nom de l'entreprise finale
        destNom = df.entreprise_finale || df.financeur_nom || '';
        destType = 'client';
        // Chercher le client pour ses infos
        const clientObj = clients.find(cl => cl.nom === df.entreprise_finale);
        destId = clientObj?.id || '';
        destAdresse = clientObj ? [clientObj.adresse, clientObj.code_postal, clientObj.ville].filter(Boolean).join(', ') : '';
        const clientContact = (clientObj?.contacts || [])[0];
        destContact = clientContact ? [clientContact.prenom, clientContact.nom].filter(Boolean).join(' ') : (clientObj?.contact_nom || '');
      } else {
        // Sous-traitance / autre: devis au nom du financeur
        destNom = df.financeur_nom || '';
        destType = 'financeur';
        destId = df.financeur_id || '';
        const finObj = financeurs.find(f => f.id === df.financeur_id);
        destAdresse = finObj ? [finObj.adresse, finObj.code_postal, finObj.ville].filter(Boolean).join(', ') : '';
        const finContact = (finObj?.contacts || [])[0];
        destContact = finContact ? [finContact.prenom, finContact.nom].filter(Boolean).join(' ') : (finObj?.contact_nom || '');
      }

      await axios.post(`${API_URL}/factures/generer/${dossierId}`, {
        type_document: type,
        dossier_financeur_id: df.id,
        destinataire_type: destType,
        destinataire_id: destId,
        destinataire_nom: destNom,
        destinataire_adresse: destAdresse || '',
        destinataire_contact: destContact || '',
        references_bdc: df.bons_commande || [],
        code_action_client: df.code_action_client || '',
        lignes: [{
          description: formData.intitule || 'Formation',
          quantite: nbCandidats,
          unite: 'stagiaire',
          prix_unitaire_ht: prixUnitaire,
          montant_ht: totalHT,
        }],
        montant_ht: totalHT,
        taux_tva: tauxTVA,
        montant_tva: montantTVA,
        montant_ttc: totalTTC,
        date_emission: new Date().toISOString().split('T')[0],
      }, { headers });
      toast.success(`${type === 'devis' ? 'Devis' : 'Convention'} generee`);
      fetchDfDocuments(editingDf.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de generation');
    } finally {
      setGeneratingDoc(null);
    }
  };

  // Ouvrir le dialogue de selection des candidats pour les convocations
  const handleOpenConvocationDialog = () => {
    if (!editingDf?.id) return;
    const candidats = editingDf.candidats || [];
    if (candidats.length === 0) {
      toast.error('Aucun candidat affecte a ce dossier financeur');
      return;
    }
    setSelectedCandidatIds(candidats.map(c => c.candidat_id));
    setConvocationMode('groupe');
    setConvocationDialog(true);
  };

  // Generer les convocations pour les candidats selectionnes
  const handleGenerateConvocations = async () => {
    if (!editingDf?.id || selectedCandidatIds.length === 0) return;
    setGeneratingDoc('convocation');
    setConvocationDialog(false);
    try {
      const res = await axios.post(`${API_URL}/convocations/generer`, {
        dossier_financeur_id: editingDf.id,
        action_formation_id: dossierId,
        candidat_ids: selectedCandidatIds,
      }, { headers });
      toast.success(`${res.data.created} convocation(s) generee(s)`);
      await fetchDfDocuments(editingDf.id);

      // Si mode groupe, telecharger le PDF combine
      if (convocationMode === 'groupe' && res.data.documents?.length > 0) {
        const docIds = res.data.documents.map(d => d.id);
        try {
          const pdfRes = await axios.post(`${API_URL}/convocations/pdf-groupe`, { document_ids: docIds }, { headers, responseType: 'blob' });
          saveAs(new Blob([pdfRes.data], { type: 'application/pdf' }), 'convocations_groupees.pdf');
        } catch (err) {
          toast.error('PDF groupe non genere, telechargez les individuellement');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de generation');
    } finally {
      setGeneratingDoc(null);
    }
  };

  // Download PDF
  const downloadPdf = async (docId, numero) => {
    const filename = `${numero || 'document'}.pdf`;
    try {
      const response = await axios.get(`${API_URL}/documents/${docId}/pdf`, { headers, responseType: 'blob' });
      saveAs(new Blob([response.data], { type: 'application/pdf' }), filename);
    } catch (err) {
      toast.error('Erreur telechargement PDF');
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API_URL}/documents/${docId}`, { headers });
      toast.success('Document supprime');
      setDeleteDocConfirm(null);
      fetchDfDocuments(editingDf?.id);
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  // Get candidats info from IDs in action
  const getCandidatInfo = (candidatId) => {
    const c = allCandidats.current.find(c => c.id === candidatId);
    return c ? `${c.nom || ''} ${c.prenom || ''}`.trim() : candidatId;
  };

  // Candidats already assigned to other dossiers financeurs
  const getAssignedCandidatIds = (excludeDfId) => {
    const ids = new Set();
    dossiersFinanceurs.forEach(df => {
      if (df.id !== excludeDfId) {
        (df.candidats || []).forEach(c => ids.add(c.candidat_id));
      }
    });
    return ids;
  };

  const handleSaveDf = async (dfData) => {
    try {
      if (dfData.id) {
        const { id, code, created_at, updated_at, ...payload } = dfData;
        await axios.put(`${API_URL}/dossiers-financeurs/${id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/dossiers-financeurs`, dfData, { headers });
      }
      toast.success('Dossier financeur sauvegardé');
      await fetchData();
      onDfsChanged?.();
      setEditingDf(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    }
  };

  const handleDeleteDf = async (dfId) => {
    try {
      await axios.delete(`${API_URL}/dossiers-financeurs/${dfId}`, { headers });
      toast.success('Dossier financeur supprimé');
      await fetchData();
      onDfsChanged?.();
    } catch { toast.error('Erreur de suppression'); }
    setShowDeleteConfirm(null);
  };

  const startNewDf = () => {
    setEditingDf({
      action_formation_id: dossierId,
      financeur_id: '',
      financeur_nom: '',
      type_financement: 'sous_traitance',
      nature_financement: '',
      entreprise_finale: '',
      candidats: [],
      modalite_facturation: 'journee',
      tarif_unitaire: 0,
      bons_de_commande: [],
      code_action_client: '',
      statut: 'brouillon',
      notes: '',
    });
  };

  // Inline creation: create financeur from the DF editor
  const handleCreateFinanceurInline = async () => {
    if (!newFinanceur.nom) {
      toast.error('Le nom du financeur est obligatoire');
      return;
    }
    setCreatingFinanceur(true);
    try {
      const response = await axios.post(`${API_URL}/financeurs`, newFinanceur, { headers });
      const created = response.data;
      // Auto-select in the current editing DF
      setEditingDf(prev => ({ ...prev, financeur_id: created.id, financeur_nom: created.nom }));
      // Notify parent to refresh financeurs list
      if (onFinanceursUpdated) onFinanceursUpdated();
      setNewFinanceur({ nom: '', type_financeur: 'opco', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '' });
      setFinanceurDialogOpen(false);
      toast.success('Financeur créé et sélectionné');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du financeur');
    } finally {
      setCreatingFinanceur(false);
    }
  };

  // Inline creation: create client (entreprise) from the DF editor
  const handleCreateClientInline = async () => {
    if (!newClient.nom) {
      toast.error("Le nom de l'entreprise est obligatoire");
      return;
    }
    setCreatingClient(true);
    try {
      const createdClientName = newClient.nom;
      await axios.post(`${API_URL}/clients`, newClient, { headers });
      // Re-fetch entreprises list FIRST to ensure the new client is in the list
      const entRes = await axios.get(`${API_URL}/entreprises`, { headers });
      setEntreprisesList(entRes.data || []);
      // Then auto-select the new enterprise name (after list is updated)
      setTimeout(() => {
        setEditingDf(prev => ({ ...prev, entreprise_finale: createdClientName }));
      }, 100);
      setNewClient({ nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '' });
      setClientDialogOpen(false);
      toast.success('Entreprise créée et sélectionnée');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création de l'entreprise");
    } finally {
      setCreatingClient(false);
    }
  };

  // ======== EDITING VIEW ========
  if (editingDf) {
    const df = editingDf;
    const assignedIds = getAssignedCandidatIds(df.id);
    const availableCandidats = candidatsAction.filter(ca => !assignedIds.has(ca.candidat_id));
    const selectedCandidatIds = new Set((df.candidats || []).map(c => c.candidat_id));

    const updateDf = (field, value) => setEditingDf(prev => ({ ...prev, [field]: value }));

    const toggleCandidat = (candidatId) => {
      const existing = df.candidats || [];
      if (selectedCandidatIds.has(candidatId)) {
        updateDf('candidats', existing.filter(c => c.candidat_id !== candidatId));
      } else {
        const allDates = (formData.planning || []).map(p => p.date).filter(Boolean);
        const defaultEmployeur = df.entreprise_finale ? (clients.find(cl => cl.nom === df.entreprise_finale) || {}).id || '' : '';
        updateDf('candidats', [...existing, {
          candidat_id: candidatId,
          type_stagiaire: 'salarie_prive',
          employeur_id: defaultEmployeur,
          dates_individuelles: allDates,
        }]);
      }
    };

    const updateCandidatField = (candidatId, field, value) => {
      updateDf('candidats', (df.candidats || []).map(c =>
        c.candidat_id === candidatId ? { ...c, [field]: value } : c
      ));
    };

    const selectFinanceur = (financeurId) => {
      const fin = financeurs.find(f => f.id === financeurId);
      if (fin) {
        setEditingDf(prev => ({ ...prev, financeur_id: financeurId, financeur_nom: fin.nom }));
      }
    };

    return (
      <>
      <Card className="border-0 shadow-sm" data-testid="dossier-financeur-editor">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingDf(null)}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <CardTitle className="text-lg">
                {df.id ? `Modifier ${df.code || 'dossier financeur'}` : 'Nouveau dossier financeur'}
              </CardTitle>
              {df.id && (
                <Select value={df.statut || 'brouillon'} onValueChange={(v) => updateDf('statut', v)} disabled={lectureSeule}>
                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="df-statut-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="facture">Facturé</SelectItem>
                    <SelectItem value="regle">Réglé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {!lectureSeule && <Button type="button" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleSaveDf(df)} data-testid="save-df-btn">
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {lectureSeule && <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="df-lecture-seule">Consultation : les dossiers financeurs ne sont modifiables que par l'administration.</p>}
          <fieldset disabled={lectureSeule} className="space-y-6 min-w-0 border-0 p-0 m-0">
          {/* Section 1: Financeur + Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Financeur *</Label>
              <div className="flex gap-2">
                <Select value={df.financeur_id || 'none'} onValueChange={(v) => v !== 'none' && selectFinanceur(v)}>
                  <SelectTrigger data-testid="df-financeur-select" className="flex-1"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Sélectionner un financeur</SelectItem>
                    {financeurs.map(f => <SelectItem key={f.id} value={f.id}>{f.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  type="button" variant="outline" size="icon"
                  className="shrink-0 border-blue-300 text-blue-600 hover:bg-blue-50"
                  onClick={() => setFinanceurDialogOpen(true)}
                  title="Créer un nouveau financeur"
                  data-testid="inline-create-financeur-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type de financement *</Label>
              <Select value={df.type_financement} onValueChange={(v) => updateDf('type_financement', v)}>
                <SelectTrigger data-testid="df-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES_FINANCEMENT.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nature du financement</Label>
              <div className="flex gap-2">
                <Select value={df.nature_financement || 'none'} onValueChange={(v) => updateDf('nature_financement', v === 'none' ? '' : v)}>
                  <SelectTrigger className="flex-1" data-testid="df-nature-select"><SelectValue placeholder="Choisir ou saisir..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {naturesFinancement.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section 2: Entreprise finale + facturation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Entreprise finale</Label>
              <div className="flex gap-2">
                <Select
                  value={df.entreprise_finale || 'none'}
                  onValueChange={(v) => updateDf('entreprise_finale', v === 'none' ? '' : v)}
                >
                  <SelectTrigger data-testid="df-entreprise-finale" className="flex-1">
                    <SelectValue placeholder="Sélectionner une entreprise..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none"><span className="text-slate-400">Aucune</span></SelectItem>
                    {entreprisesList.map(e => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button" variant="outline" size="icon"
                  className="shrink-0 border-amber-300 text-amber-600 hover:bg-amber-50"
                  onClick={() => setClientDialogOpen(true)}
                  title="Créer une nouvelle entreprise"
                  data-testid="inline-create-client-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400">Pour sous-traitance: l'entreprise pour laquelle vous formez</p>
            </div>
            <div className="space-y-2">
              <Label>Modalité de facturation</Label>
              <Select value={df.modalite_facturation} onValueChange={(v) => updateDf('modalite_facturation', v)}>
                <SelectTrigger data-testid="df-modalite-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODALITES_FACTURATION.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {voirCouts && (
            <div className="space-y-2">
              <Label>Tarif unitaire HT ({df.modalite_facturation === 'forfait' ? 'forfait' : 'par unité'})</Label>
              <Input
                type="number" min={0} step={0.01}
                value={df.tarif_unitaire || ''}
                onChange={(e) => updateDf('tarif_unitaire', parseFloat(e.target.value) || 0)}
                data-testid="df-tarif"
              />
            </div>
            )}
            <div className="space-y-2">
              <Label>Nb candidats prevus</Label>
              <Input
                type="number" min={0}
                value={df.nb_candidats_prevus ?? ''}
                onChange={(e) => updateDf('nb_candidats_prevus', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Si candidats non encore renseignes"
                data-testid="df-nb-candidats-prevus"
              />
            </div>
          </div>

          {/* Section 3: Code action client + Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Code action client</Label>
              <Input
                placeholder="Référence du client..."
                value={df.code_action_client || ''}
                onChange={(e) => updateDf('code_action_client', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="Notes internes..."
                value={df.notes || ''}
                onChange={(e) => updateDf('notes', e.target.value)}
              />
            </div>
          </div>

          {/* Section 3b: Bons de Commande */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Bons de commande</Label>
              {!lectureSeule && <Button type="button" variant="outline" size="sm" onClick={() => {
                const bdcs = df.bons_de_commande || [];
                updateDf('bons_de_commande', [...bdcs, {
                  id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
                  numero_bdc: '',
                  donneur_ordre_nom: df.financeur_nom || '',
                  tarif: df.tarif_unitaire || 0,
                  unite: df.modalite_facturation === 'journee' ? 'jour' : 'heure',
                  quantite: 0,
                  total: 0,
                  date_debut: formData.date_debut || '',
                  date_fin: formData.date_fin || '',
                  mois: null,
                  commentaire: '',
                }]);
              }} data-testid="add-bdc-btn">
                <Plus className="w-3 h-3 mr-1" /> Ajouter un BDC
              </Button>}
            </div>
            {(df.bons_de_commande || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun bon de commande</p>
            ) : (
              <div className="space-y-3">
                {(df.bons_de_commande || []).map((bdc, bi) => (
                  <div key={bdc.id || bi} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">BDC #{bi + 1}</span>
                      {!lectureSeule && <Button type="button" variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-6 w-6 p-0" onClick={() => {
                        updateDf('bons_de_commande', (df.bons_de_commande || []).filter((_, i) => i !== bi));
                      }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">N° BDC</Label>
                        <Input className="h-8 text-xs" placeholder="Numéro..." value={bdc.numero_bdc || ''}
                          onChange={(e) => {
                            const bdcs = [...(df.bons_de_commande || [])];
                            bdcs[bi] = { ...bdcs[bi], numero_bdc: e.target.value };
                            updateDf('bons_de_commande', bdcs);
                          }}
                          data-testid={`bdc-numero-${bi}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Mois de rattachement</Label>
                        <Input className="h-8 text-xs" type="month" value={bdc.mois || ''} title="Pré-coche ce BDC sur la facture de ce mois"
                          onChange={(e) => {
                            const bdcs = [...(df.bons_de_commande || [])];
                            bdcs[bi] = { ...bdcs[bi], mois: e.target.value || null };
                            updateDf('bons_de_commande', bdcs);
                          }}
                          data-testid={`bdc-mois-${bi}`}
                        />
                      </div>
                      {voirCouts && <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Tarif unitaire</Label>
                        <Input className="h-8 text-xs" type="number" min={0} step={0.01} value={bdc.tarif || ''}
                          onChange={(e) => {
                            const bdcs = [...(df.bons_de_commande || [])];
                            const tarif = parseFloat(e.target.value) || 0;
                            bdcs[bi] = { ...bdcs[bi], tarif, total: tarif * (bdcs[bi].quantite || 0) };
                            updateDf('bons_de_commande', bdcs);
                          }}
                        />
                      </div>}
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Quantité</Label>
                        <Input className="h-8 text-xs" type="number" min={0} value={bdc.quantite || ''}
                          onChange={(e) => {
                            const bdcs = [...(df.bons_de_commande || [])];
                            const quantite = parseFloat(e.target.value) || 0;
                            bdcs[bi] = { ...bdcs[bi], quantite, total: (bdcs[bi].tarif || 0) * quantite };
                            updateDf('bons_de_commande', bdcs);
                          }}
                        />
                      </div>
                      {voirCouts && <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Total HT</Label>
                        <div className="h-8 flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 rounded px-2">
                          {(bdc.total || 0).toFixed(2)} €
                        </div>
                      </div>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Commentaire</Label>
                      <Input className="h-8 text-xs" placeholder="Commentaire..." value={bdc.commentaire || ''}
                        onChange={(e) => {
                          const bdcs = [...(df.bons_de_commande || [])];
                          bdcs[bi] = { ...bdcs[bi], commentaire: e.target.value };
                          updateDf('bons_de_commande', bdcs);
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 4: Candidats */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Candidats concernés par ce financement</Label>

            {/* Batch assignment toolbar */}
            {(df.candidats || []).length > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2" data-testid="batch-assignment">
                <p className="text-xs font-semibold text-indigo-700">Affectation groupée ({(df.candidats || []).length} candidat(s) sélectionné(s))</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1 min-w-[250px]">
                    <Label className="text-xs text-indigo-600">Type de stagiaire</Label>
                    <Select onValueChange={(v) => {
                      updateDf('candidats', (df.candidats || []).map(c => ({ ...c, type_stagiaire: v })));
                      toast.success('Type appliqué à tous les candidats');
                    }}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="batch-type-stagiaire"><SelectValue placeholder="Appliquer à tous..." /></SelectTrigger>
                      <SelectContent>
                        {TYPES_STAGIAIRE.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 min-w-[250px]">
                    <Label className="text-xs text-indigo-600">Employeur</Label>
                    <Select onValueChange={(v) => {
                      updateDf('candidats', (df.candidats || []).map(c => ({ ...c, employeur_id: v === 'none' ? '' : v })));
                      toast.success('Employeur appliqué à tous les candidats');
                    }}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="batch-employeur"><SelectValue placeholder="Appliquer à tous..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {clients.map(cl => <SelectItem key={cl.id} value={cl.id}>{cl.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => {
                      const allDates = (formData.planning || []).map(p => p.date).filter(Boolean);
                      updateDf('candidats', (df.candidats || []).map(c => ({ ...c, dates_individuelles: allDates })));
                      toast.success('Toutes les dates affectées');
                    }}>Toutes les dates</Button>
                  </div>
                </div>
              </div>
            )}

            {candidatsAction.length === 0 ? (
              <p className="text-sm text-slate-400 italic">Aucun candidat affecté à cette action de formation. Ajoutez-en d'abord dans l'onglet "Candidats".</p>
            ) : (
              <div className="space-y-2">
                {/* Select all toggle */}
                {availableCandidats.length > 0 && (
                  <div
                    className="flex items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                    data-testid="select-all-candidats"
                    onClick={() => {
                      const allAvailableIds = availableCandidats.map(ca => ca.candidat_id);
                      const allSelected = allAvailableIds.every(id => selectedCandidatIds.has(id));
                      if (allSelected) {
                        updateDf('candidats', (df.candidats || []).filter(c => !allAvailableIds.includes(c.candidat_id)));
                      } else {
                        const existing = df.candidats || [];
                        const allDates = (formData.planning || []).map(p => p.date).filter(Boolean);
                        const toAdd = allAvailableIds.filter(id => !selectedCandidatIds.has(id)).map(id => ({
                          candidat_id: id,
                          type_stagiaire: 'salarie_prive',
                          employeur_id: df.entreprise_finale ? (clients.find(cl => cl.nom === df.entreprise_finale) || {}).id || '' : '',
                          dates_individuelles: allDates,
                        }));
                        updateDf('candidats', [...existing, ...toAdd]);
                      }
                    }}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      availableCandidats.length > 0 && availableCandidats.every(ca => selectedCandidatIds.has(ca.candidat_id))
                        ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                    }`}>
                      {availableCandidats.length > 0 && availableCandidats.every(ca => selectedCandidatIds.has(ca.candidat_id)) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Tous ({availableCandidats.length})</span>
                  </div>
                )}
                {availableCandidats.map(ca => {
                  const isSelected = selectedCandidatIds.has(ca.candidat_id);
                  const dfCandidat = (df.candidats || []).find(c => c.candidat_id === ca.candidat_id);
                  const planningDates = (formData.planning || []).map(p => p.date).filter(Boolean).sort();
                  const candidatDates = new Set(dfCandidat?.dates_individuelles || []);
                  const employeurClient = dfCandidat?.employeur_id ? clients.find(cl => cl.id === dfCandidat.employeur_id) : null;

                  return (
                    <div key={ca.candidat_id} className={`border rounded-lg transition-colors ${isSelected ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200'}`}>
                      <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer" onClick={() => toggleCandidat(ca.candidat_id)}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm font-medium flex-1 flex items-center gap-2 flex-wrap"><CandidatLink dossierId={dossierId} candidatId={ca.candidat_id}>{getCandidatInfo(ca.candidat_id)}</CandidatLink>
                          {ca.abandon && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" title="Candidat ayant abandonné : facturation à ajuster manuellement (prorata)" data-testid={`df-abandon-badge-${ca.candidat_id}`}>Abandon le {ca.abandon.date?.split('-').reverse().join('/')} — à facturer au prorata</span>}
                        </span>
                        {isSelected && dfCandidat && (
                          <span className="text-[10px] text-slate-400">
                            {candidatDates.size}/{planningDates.length} jours
                            {employeurClient && ` | ${employeurClient.nom}`}
                          </span>
                        )}
                        {assignedIds.has(ca.candidat_id) && !isSelected && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Déjà affecté</span>
                        )}
                      </div>
                      {isSelected && dfCandidat && (
                        <div className="px-4 pb-3 pt-1 border-t border-indigo-100 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Type de stagiaire</Label>
                              <Select value={dfCandidat.type_stagiaire} onValueChange={(v) => updateCandidatField(ca.candidat_id, 'type_stagiaire', v)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {TYPES_STAGIAIRE.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Employeur</Label>
                              <Select value={dfCandidat.employeur_id || 'none'} onValueChange={(v) => updateCandidatField(ca.candidat_id, 'employeur_id', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Non renseigné</SelectItem>
                                  {clients.map(cl => <SelectItem key={cl.id} value={cl.id}>{cl.nom}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {/* Dates selection */}
                          {planningDates.length > 0 && (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs text-slate-500">Dates de présence ({candidatDates.size}/{planningDates.length})</Label>
                                <div className="flex gap-1">
                                  <button className="text-[10px] text-indigo-600 hover:underline" onClick={() => updateCandidatField(ca.candidat_id, 'dates_individuelles', [...planningDates])}>Toutes</button>
                                  <span className="text-[10px] text-slate-300">|</span>
                                  <button className="text-[10px] text-red-500 hover:underline" onClick={() => updateCandidatField(ca.candidat_id, 'dates_individuelles', [])}>Aucune</button>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                {planningDates.map(date => {
                                  const isActive = candidatDates.has(date);
                                  const d = new Date(date);
                                  const label = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                                  return (
                                    <button
                                      key={date}
                                      type="button"
                                      onClick={() => {
                                        const newDates = isActive
                                          ? [...candidatDates].filter(dd => dd !== date)
                                          : [...candidatDates, date];
                                        updateCandidatField(ca.candidat_id, 'dates_individuelles', newDates);
                                      }}
                                      className={`px-2 py-1 rounded text-[10px] font-mono border transition-colors ${
                                        isActive ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                                      }`}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Show already-assigned candidats that belong to other DFs */}
                {candidatsAction.filter(ca => assignedIds.has(ca.candidat_id) && !selectedCandidatIds.has(ca.candidat_id)).length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">
                    {candidatsAction.filter(ca => assignedIds.has(ca.candidat_id) && !selectedCandidatIds.has(ca.candidat_id)).length} candidat(s) déjà affecté(s) à d'autres dossiers financeurs
                  </p>
                )}
              </div>
            )}
          </div>

          </fieldset>
          {/* ====== SECTION DOCUMENTS DU DF (telechargement autorise en lecture seule) ====== */}
          {editingDf.id && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents
                </h3>
                {!lectureSeule && <div className="flex gap-2">
                  {voirCouts && <><Button type="button" size="sm" variant="outline" className="text-xs h-7"
                    onClick={() => handleGenerateDocument('devis')}
                    disabled={!!generatingDoc}
                    data-testid="gen-devis-btn">
                    {generatingDoc === 'devis' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                    Devis
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-xs h-7"
                    onClick={() => handleGenerateDocument('convention')}
                    disabled={!!generatingDoc}
                    data-testid="gen-convention-btn">
                    {generatingDoc === 'convention' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileCheck className="w-3 h-3 mr-1" />}
                    Convention
                  </Button></>}
                  <Button type="button" size="sm" variant="outline" className="text-xs h-7"
                    onClick={handleOpenConvocationDialog}
                    disabled={!!generatingDoc || (editingDf.candidats || []).length === 0}
                    data-testid="gen-convocations-btn">
                    {generatingDoc === 'convocation' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Users className="w-3 h-3 mr-1" />}
                    Convocations
                  </Button>
                </div>}
              </div>

              {dfDocuments.length > 0 ? (
                <div className="space-y-1">
                  {dfDocuments.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded font-medium text-[10px] ${
                          doc.type_document === 'devis' ? 'bg-blue-100 text-blue-700' :
                          doc.type_document === 'convention' ? 'bg-green-100 text-green-700' :
                          doc.type_document === 'convocation' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {doc.type_document === 'devis' ? 'Devis' :
                           doc.type_document === 'convention' ? 'Convention' :
                           doc.type_document === 'convocation' ? 'Convocation' :
                           doc.type_document}
                        </span>
                        <span className="font-mono text-slate-600">{doc.numero}</span>
                        {doc.destinataire_nom && (
                          <span className="text-slate-400">- {doc.destinataire_nom}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => downloadPdf(doc.id, doc.numero)}
                          className="p-1 rounded hover:bg-indigo-100 text-indigo-600" title="Télécharger"
                          data-testid={`download-doc-${doc.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {!lectureSeule && <button type="button" onClick={() => setDeleteDocConfirm(doc)}
                          className="p-1 rounded hover:bg-red-100 text-red-500" title="Supprimer"
                          data-testid={`delete-doc-${doc.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 text-center py-3">Aucun document généré pour ce dossier financeur</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Nouveau Financeur (inline) */}
      <Dialog open={financeurDialogOpen} onOpenChange={setFinanceurDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" />
              Nouveau financeur
            </DialogTitle>
            <DialogDescription>
              Créez un nouveau financeur qui sera automatiquement sélectionné
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newFinanceur.nom}
                  onChange={(e) => setNewFinanceur({ ...newFinanceur, nom: e.target.value })}
                  placeholder="Ex: OPCO Atlas"
                  data-testid="inline-financeur-nom"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={newFinanceur.type_financeur} onValueChange={(v) => setNewFinanceur({ ...newFinanceur, type_financeur: v })}>
                  <SelectTrigger data-testid="inline-financeur-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES_FINANCEUR.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={newFinanceur.adresse} onChange={(e) => setNewFinanceur({ ...newFinanceur, adresse: e.target.value })} placeholder="123 rue de la Formation" data-testid="inline-financeur-adresse" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input value={newFinanceur.code_postal} onChange={(e) => setNewFinanceur({ ...newFinanceur, code_postal: e.target.value })} placeholder="75001" data-testid="inline-financeur-cp" />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input value={newFinanceur.ville} onChange={(e) => setNewFinanceur({ ...newFinanceur, ville: e.target.value })} placeholder="Paris" data-testid="inline-financeur-ville" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newFinanceur.email} onChange={(e) => setNewFinanceur({ ...newFinanceur, email: e.target.value })} placeholder="contact@opco.fr" data-testid="inline-financeur-email" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={newFinanceur.telephone} onChange={(e) => setNewFinanceur({ ...newFinanceur, telephone: e.target.value })} placeholder="01 23 45 67 89" data-testid="inline-financeur-tel" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>SIRET</Label>
              <Input value={newFinanceur.siret} onChange={(e) => setNewFinanceur({ ...newFinanceur, siret: e.target.value })} placeholder="123 456 789 00012" data-testid="inline-financeur-siret" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFinanceurDialogOpen(false)}>Annuler</Button>
            <Button type="button" className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateFinanceurInline} disabled={creatingFinanceur} data-testid="submit-inline-financeur">
              {creatingFinanceur ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : <><Plus className="w-4 h-4 mr-2" />Créer et sélectionner</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nouvelle Entreprise / Client (inline) */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              Nouvelle entreprise
            </DialogTitle>
            <DialogDescription>
              Créez une nouvelle entreprise qui sera automatiquement sélectionnée
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nom de l'entreprise *</Label>
              <Input value={newClient.nom} onChange={(e) => setNewClient({ ...newClient, nom: e.target.value })} placeholder="Ex: Entreprise ABC" data-testid="inline-client-nom" />
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={newClient.adresse} onChange={(e) => setNewClient({ ...newClient, adresse: e.target.value })} placeholder="456 avenue des Entreprises" data-testid="inline-client-adresse" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input value={newClient.code_postal} onChange={(e) => setNewClient({ ...newClient, code_postal: e.target.value })} placeholder="75001" data-testid="inline-client-cp" />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input value={newClient.ville} onChange={(e) => setNewClient({ ...newClient, ville: e.target.value })} placeholder="Paris" data-testid="inline-client-ville" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="contact@entreprise.fr" data-testid="inline-client-email" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={newClient.telephone} onChange={(e) => setNewClient({ ...newClient, telephone: e.target.value })} placeholder="01 23 45 67 89" data-testid="inline-client-tel" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>SIRET</Label>
              <Input value={newClient.siret} onChange={(e) => setNewClient({ ...newClient, siret: e.target.value })} placeholder="987 654 321 00034" data-testid="inline-client-siret" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)}>Annuler</Button>
            <Button type="button" className="bg-amber-600 hover:bg-amber-700" onClick={handleCreateClientInline} disabled={creatingClient} data-testid="submit-inline-client">
              {creatingClient ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : <><Plus className="w-4 h-4 mr-2" />Créer et sélectionner</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: Confirmer suppression document */}
      <AlertDialog open={!!deleteDocConfirm} onOpenChange={(open) => { if (!open) setDeleteDocConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le document "{deleteDocConfirm?.numero}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteDocument(deleteDocConfirm?.id)} data-testid="confirm-delete-doc">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
    );
  }

  // ======== LIST VIEW ========
  if (loading) return <Card><CardContent className="py-8 text-center text-slate-400">Chargement...</CardContent></Card>;

  const totalCandidatsCouverts = dossiersFinanceurs.reduce((s, df) => s + (df.candidats?.length || 0), 0);
  const candidatsNonCouverts = candidatsAction.length - totalCandidatsCouverts;

  return (
    <Card className="border-0 shadow-sm" data-testid="dossiers-financeurs-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Dossiers financeurs</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {dossiersFinanceurs.length} dossier(s) — {totalCandidatsCouverts}/{candidatsAction.length} candidat(s) couvert(s)
              {candidatsNonCouverts > 0 && (
                <span className="text-amber-600 ml-2">({candidatsNonCouverts} sans financement)</span>
              )}
            </p>
          </div>
          {!lectureSeule && <Button type="button" size="sm" onClick={startNewDf} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-df-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau dossier financeur
          </Button>}
        </div>
      </CardHeader>
      <CardContent>
        {dossiersFinanceurs.length === 0 ? (
          <div className="text-center py-10">
            <CreditCard className="w-12 h-12 mx-auto text-slate-200 mb-3" />
            <p className="text-slate-500 mb-1">Aucun dossier financeur</p>
            <p className="text-xs text-slate-400 mb-4">Créez un dossier financeur pour chaque source de financement</p>
            <Button type="button" variant="outline" size="sm" onClick={startNewDf}>
              <Plus className="w-4 h-4 mr-1" /> Créer le premier
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {dossiersFinanceurs.map(df => {
              const typeLabel = TYPES_FINANCEMENT.find(t => t.value === df.type_financement)?.label || df.type_financement;
              const modaliteLabel = MODALITES_FACTURATION.find(m => m.value === df.modalite_facturation)?.label || df.modalite_facturation;
              return (
                <div
                  key={df.id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors cursor-pointer group"
                  data-testid={`df-card-${df.id}`}
                  onClick={() => setEditingDf(df)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{df.code}</span>
                        <span className="text-sm font-semibold text-slate-800">{df.financeur_nom || 'Financeur non défini'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          df.statut === 'brouillon' ? 'bg-slate-100 text-slate-600' :
                          df.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                          df.statut === 'facture' ? 'bg-amber-100 text-amber-700' :
                          df.statut === 'regle' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {df.statut === 'brouillon' ? 'Brouillon' : df.statut === 'en_cours' ? 'En cours' : df.statut === 'facture' ? 'Facturé' : df.statut === 'regle' ? 'Réglé' : 'Annulé'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>{typeLabel}</span>
                        {df.nature_financement && <span className="text-indigo-500">{df.nature_financement}</span>}
                        <span>{(df.candidats?.length || 0)} candidat(s){df.nb_candidats_prevus ? ` (${df.nb_candidats_prevus} prevus)` : ''}</span>
                        <span>{modaliteLabel}{voirCouts ? ` — ${df.tarif_unitaire || 0} € HT` : ''}</span>
                      </div>
                      {df.entreprise_finale && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md w-fit">
                          <Building2 className="w-3 h-3" />
                          <span className="font-medium">{df.entreprise_finale}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditingDf(df)} title={lectureSeule ? 'Consulter' : 'Modifier'}>
                        <FileText className="w-4 h-4" />
                      </Button>
                      {!lectureSeule && <Button type="button" variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => setShowDeleteConfirm(df)} title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </Button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Delete confirm */}
        {showDeleteConfirm && (
          <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Supprimer ce dossier financeur ?</DialogTitle>
                <DialogDescription>
                  Le dossier "{showDeleteConfirm.code}" ({showDeleteConfirm.financeur_nom}) sera définitivement supprimé.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDeleteConfirm(null)}>Annuler</Button>
                <Button type="button" className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteDf(showDeleteConfirm.id)}>Supprimer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Convocation Selection Dialog */}
        <Dialog open={convocationDialog} onOpenChange={setConvocationDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Generer les convocations</DialogTitle>
              <DialogDescription>Selectionnez les candidats et le mode de generation.</DialogDescription>
            </DialogHeader>
            {editingDf && (
              <div className="space-y-4">
                {/* Mode selection */}
                <div className="flex gap-2">
                  <Button type="button"
                    variant={convocationMode === 'groupe' ? 'default' : 'outline'}
                    size="sm"
                    className={convocationMode === 'groupe' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    onClick={() => setConvocationMode('groupe')}
                    data-testid="conv-mode-groupe"
                  >
                    <FileDown className="w-4 h-4 mr-1.5" /> PDF unique (groupe)
                  </Button>
                  <Button type="button"
                    variant={convocationMode === 'individuel' ? 'default' : 'outline'}
                    size="sm"
                    className={convocationMode === 'individuel' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    onClick={() => setConvocationMode('individuel')}
                    data-testid="conv-mode-individuel"
                  >
                    <Users className="w-4 h-4 mr-1.5" /> PDF individuels
                  </Button>
                </div>

                {/* Select all / none */}
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedCandidatIds.length} / {(editingDf.candidats || []).length} selectionne(s)
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" size="sm" className="text-xs h-7"
                      onClick={() => setSelectedCandidatIds((editingDf.candidats || []).map(c => c.candidat_id))}
                      data-testid="conv-select-all"
                    >
                      Tout cocher
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="text-xs h-7"
                      onClick={() => setSelectedCandidatIds([])}
                      data-testid="conv-select-none"
                    >
                      Tout decocher
                    </Button>
                  </div>
                </div>

                {/* Candidate list */}
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {(editingDf.candidats || []).map((c) => {
                    const cand = allCandidats.current.find(ca => ca.id === c.candidat_id);
                    const name = getCandidatInfo(c.candidat_id);
                    const checked = selectedCandidatIds.includes(c.candidat_id);
                    return (
                      <label
                        key={c.candidat_id}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50 hover:bg-slate-100 border border-transparent'}`}
                        data-testid={`conv-candidat-${c.candidat_id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(val) => {
                            setSelectedCandidatIds(prev =>
                              val ? [...prev, c.candidat_id] : prev.filter(id => id !== c.candidat_id)
                            );
                          }}
                        />
                        <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-[10px] flex items-center justify-center font-semibold flex-shrink-0">
                          {(cand?.prenom?.[0] || '').toUpperCase()}{(cand?.nom?.[0] || '').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
                          {c.employeur && <p className="text-[11px] text-slate-400 truncate">{c.employeur}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConvocationDialog(false)}>Annuler</Button>
              <Button type="button"
                onClick={handleGenerateConvocations}
                disabled={selectedCandidatIds.length === 0 || !!generatingDoc}
                className="bg-indigo-600 hover:bg-indigo-700 gap-2"
                data-testid="conv-generate-btn"
              >
                {generatingDoc === 'convocation' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
                Generer ({selectedCandidatIds.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export { FinancementTab };
